import React from 'react';
import { Stack, Typography, List } from '@mui/material';
import PageContainer from 'pages/PageContainer';
import { PrivacyPolicyLists } from 'shared/constants/legal-pages-constants/privacy-policy-constants';
import { HomeLink } from 'components/LegalPagesLinks/HomeLink';
import { MailToLink } from 'components/LegalPagesLinks/MailToLink';
import { CustomListItem } from 'components/List/CustomListItem/CustomListItem';

const PrivacyPolicy: React.FunctionComponent = () => (
  <PageContainer showMenu={false}>
    <Stack py={{ xs: 0, md: 4 }} rowGap="64px" width="100%" maxWidth="1200px" mx="auto">
      <Typography variant="h1">Privacy Policy</Typography>
      <Stack rowGap="16px">
        <Typography variant="body2">Effective Date: September 1, 2025</Typography>
        <Typography variant="body2">
          {'Deb8.TV LLC ("we," "our," or "us") operates the '}
          <HomeLink />
          {` online video debate platform. This Privacy Policy explains how we collect, use, and share
          personal information when you use our website, mobile applications, and related services
          (collectively, the "Services").`}
        </Typography>
        <Typography variant="body2">
          We are committed to protecting your privacy and handling your information responsibly. By
          using our Services, you agree to the practices described in this Policy.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          1. Information We Collect
        </Typography>
        <Typography variant="body2">
          We collect the following categories of personal information:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.InformationWeCollect.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          2. How We Use Information
        </Typography>
        <Typography variant="body2">We use your information to:</Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.HowWeUseInformation.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          3. Sharing of Information
        </Typography>
        <Typography variant="body2">
          We do not sell personal information. We may share information in the following situations:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.SharingOfInformation.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          4. Cookies and Tracking
        </Typography>
        <Typography variant="body2">We use cookies and similar technologies to: </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.CookiesAndTracking.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
        <Typography variant="body2">
          You may control cookies through your browser settings, but disabling them may limit the
          functionality of our Services.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          5. Your Rights
        </Typography>
        <Typography variant="body2">
          Depending on your location (including California under the CCPA/CPRA), you may have rights
          such as:
        </Typography>
        <List className="list-legal-pages">
          {PrivacyPolicyLists.YourRights.map((item, index) => (
            <CustomListItem key={index} {...item} />
          ))}
        </List>
        <Typography variant="body2">
          {'To exercise these rights, email us at '}
          <MailToLink />
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          6. Children’s Privacy
        </Typography>
        <Typography variant="body2">
          Our Services are not directed to children under 13 (or under 16 in California). We do not
          knowingly collect personal information from children. If we learn that a child’s
          information has been collected, we will delete it.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          7. Data Retention
        </Typography>
        <Typography variant="body2">
          We retain personal information for as long as necessary to provide the Services, comply
          with legal obligations, resolve disputes, and enforce agreements.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          8. Security
        </Typography>
        <Typography variant="body2">
          We implement reasonable safeguards to protect your personal information. However, no
          system is 100% secure, and we cannot guarantee absolute security.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          9. Third-Party Links
        </Typography>
        <Typography variant="body2">
          Our Services may link to third-party websites or services. We are not responsible for
          their privacy practices. Please review their policies.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          10. Changes to This Policy
        </Typography>
        <Typography variant="body2">
          We may update this Privacy Policy from time to time. If we make material changes, we will
          notify you by posting the updated policy with a new effective date.
        </Typography>
      </Stack>

      <Stack rowGap="24px">
        <Typography variant="h2" className="legal-page-title">
          11. Contact Us
        </Typography>
        <Typography variant="body2">
          If you have questions or requests regarding this Privacy Policy, contact us at:
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          Deb8.TV LLC
        </Typography>
        <Typography variant="body2">
          {'Email: '}
          <MailToLink />
        </Typography>
      </Stack>
    </Stack>
  </PageContainer>
);

export default PrivacyPolicy;
